import { isMobileApp } from '@utils/environment';
import { IPrismicLink } from '@monofront/types';
import { linkResolver, mobileLinkResolver } from '@utils/prismic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAuthentication } from '@context/businessContainer';
import { useMemo } from 'react';
import { FilledContentRelationshipField, isFilled } from '@prismicio/client';

interface IPrismicLinkProps {
  link: IPrismicLink;
  className?: string;
  trackingId?: string;
  children: React.ReactNode;
}

interface ITypeformPrismicLink {
  /**
   * URL example: https://lafourche.typeform.com/<id>#user_email,user_id
   */
  typeformUrl: string;
  target?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 *  Don't forget to keep doc in sync: https://www.notion.so/Typeform-Linking-bfc5f2ba357f4946b2f528f1af1feb89
 */
const TypeformPrismicLink = ({
  typeformUrl,
  target,
  className,
  children,
}: ITypeformPrismicLink) => {
  const { customer } = useAuthentication();

  const href = useMemo(() => {
    const [baseUrl, variables] = typeformUrl.split('#');
    const url = new URL(baseUrl);
    const params = new URLSearchParams();

    if (customer && variables) {
      variables.split(',').forEach((variable) => {
        switch (variable) {
          case 'user_email':
            params.set('user_email', customer.email);
            break;
          case 'user_id':
            params.set('user_id', customer.uid);
            break;
          case 'order_quantity':
            params.set('order_quantity', customer.totalOrderPaid.toString(10));
            break;
          default:
            throw new Error(`Unknown Typeform variable ${variable}`);
        }
      });
    }

    url.hash = params.toString();

    return url.toString();
  }, [typeformUrl, customer]);

  return (
    <a
      href={href}
      target={target || '_self'}
      className={className}
      data-testid={`PrismicLink-go-to-${typeformUrl}`}
    >
      {children}
    </a>
  );
};

const PrismicLink: React.FC<IPrismicLinkProps> = ({
  link,
  children,
  className,
  trackingId,
}) => {
  const router = useRouter();

  if (link.link_type === 'Any') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (isMobileApp && router.pathname.startsWith('/checkout')) {
    return (
      <a
        href={mobileLinkResolver(link)}
        className={className}
        data-testid={`PrismicLink-go-to-${mobileLinkResolver(link)}`}
      >
        {children}
      </a>
    );
  }

  if (
    link.link_type === 'Web' &&
    isFilled.link(link) &&
    link.url.startsWith('#')
  ) {
    return (
      <a
        className={className}
        href={link.url}
        data-testid={`PrismicLink-go-to-${link.url}`}
      >
        {children}
      </a>
    );
  }

  const splittedUrl =
    link.link_type === 'Web' && 'url' in link && link.url
      ? new URL(link.url).pathname.split('/')
      : [];

  if (
    link.link_type === 'Web' &&
    (!('target' in link) || link.target !== '_blank') &&
    splittedUrl.length &&
    splittedUrl.some((path) => path === 'collections' || path === 'products')
  ) {
    return (
      <Link
        href={`/${splittedUrl[1]}/${splittedUrl[2]}${
          trackingId ? `?trackingId=${trackingId}` : ''
        }`}
        className={className}
        data-testid={`PrismicLink-go-to-${splittedUrl[1]}-${splittedUrl[2]}-1`}
      >
        {children}
      </Link>
    );
  }

  if (
    link.link_type === 'Web' &&
    'url' in link &&
    link.url.startsWith('https://lafourche.typeform.com/')
  ) {
    return (
      <TypeformPrismicLink
        typeformUrl={link.url}
        target={link.target}
        className={className}
      >
        {children}
      </TypeformPrismicLink>
    );
  }

  if (
    link.link_type === 'Web' ||
    (link.link_type === 'Document' &&
      'type' in link &&
      ['blogpost', 'blogcategory', 'bloghome'].includes(link.type))
  ) {
    const target =
      link.link_type === 'Web' && 'target' in link && link.target
        ? link.target
        : '_self';

    return (
      <a
        href={
          link.link_type === 'Web' && 'url' in link
            ? link.url
            : linkResolver(link as FilledContentRelationshipField, trackingId)
        }
        target={target}
        // TODO: handle .link classe here https://gitlab.com/la-fourche/scrum/-/issues/1760
        className={className}
        data-testid={`PrismicLink-go-to-${
          link.link_type === 'Web' && 'url' in link
            ? link.url
            : linkResolver(link as FilledContentRelationshipField, trackingId)
        }`}
      >
        {children}
      </a>
    );
  }

  const resolvedLink = linkResolver(
    link as FilledContentRelationshipField,
    trackingId,
  );

  if (resolvedLink.startsWith('http')) {
    return (
      <a
        href={resolvedLink}
        className={className}
        data-testid={`PrismicLink-go-to-resolvedLink-${resolvedLink}`}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={resolvedLink}
      className={className}
      data-testid={`PrismicLink-go-to-resolvedLink-${resolvedLink}`}
    >
      {children}
    </Link>
  );
};

export default PrismicLink;
